// The event → invalidation registry. Lives under app/ (the wrapper layer), NOT features/, so it is
// outside the no-raw-useQuery lint scope — and it uses queryClient.invalidateQueries, never useQuery. Plane-B
// signals carry NO domain data ({type,id,v,t}); the registry translates each frozen dash-form `type`
// into a cache action, and REST is always the source of truth for entity state.
import type { QueryClient } from "@tanstack/react-query";
import type { UiSignal, UiSignalType } from "@cntrl/api-client";

/**
 * The tenant-keyed guest-list query key: <c>['band', bandId, 'show', showId, 'guests']</c>. The
 * <c>useGuestList</c> hook builds its key from here; the registry invalidates the same shape.
 */
export const guestListKey = (bandId: string, showId: string) =>
  ["band", bandId, "show", showId, "guests"] as const;

/** True iff a query key is a guest-list key for <paramref name="showId"/> — `['band', *, 'show', showId, 'guests']`. */
export function isGuestListKey(key: readonly unknown[], showId: string): boolean {
  return (
    key.length === 5 &&
    key[0] === "band" &&
    key[2] === "show" &&
    key[3] === showId &&
    key[4] === "guests"
  );
}

/**
 * The tenant-keyed road-cases query key: <c>['band', bandId, 'road-cases']</c>. The
 * <c>useRoadCases</c> hook + the mobile Case-Checklist widget build their key from here; the registry
 * invalidates the same shape on <c>road-cases-changed</c>.
 */
export const roadCasesKey = (bandId: string) => ["band", bandId, "road-cases"] as const;

/** A signal handler: translate one plane-B signal into a cache action. */
type SignalHandler = (qc: QueryClient, signal: UiSignal) => void;

/**
 * `guests-changed` / `guest-scanned` → invalidate the show's guest list.
 *
 * UNCONDITIONALLY — no per-signal v-drop. The guest list is a show-scoped LIST topic, and `v` is a per-GUEST
 * row version, so two guests can carry `v=0`; there is no monotonic topic-level version to compare
 * (+ the row). A per-topic v-drop would silently lose the second guest. So every
 * guest signal triggers a REST refetch of the list.
 *
 * The signal carries only the showId (`id` = the `ac.ui.show.{showId}` scope), never a bandId, so we
 * invalidate every guest-list key for that show by predicate: a show belongs to exactly one band, and
 * switchBand scorches the cache, so at most one band's key is ever present. The `compsCount` rides
 * the refetched `{items, compsCount}` payload; the direct comps-counter patch ('s "one direct cache
 * write") is the guest-store optimization.
 */
const invalidateGuestList: SignalHandler = (qc, signal) => {
  // `slice(0, 5)`: the list AND its children — the partner chips' `onList` (ART-239) rides the same signal.
  void qc.invalidateQueries({ predicate: (query) => isGuestListKey(query.queryKey.slice(0, 5), signal.id) });
};

/**
 * `schedule-changed` → invalidate the band's shows list + every open show record.
 *
 * The signal rides `ac.ui.band.{bandId}`, so `id` is the bandId (a band-group signal is id-only — no
 * filtering beyond join). We can't know WHICH show changed, so we invalidate coarsely: the list keys
 * `['band', bandId, 'shows', …]` (partial match → every filtered view) AND the show-record keys
 * `['band', bandId, 'show', …]`. REST is the source of truth; a redundant refetch is cheap and never wrong.
 */
const invalidateSchedule: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "shows"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "show"] });
  // The dedicated holds funnel key is NOT a child of 'shows'/'show', so it needs its own line
  // a confirm flips a hold out of the funnel, and RSVP/hold writes ride `schedule-changed`.
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "holds"] });
  // …and the personal invite feed on the Opportunities card, which is its own key for the same reason: a
  // teammate's RSVP moves the rings on your row, and a promote flips the row's pill SHOW HOLD → SHOW.
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "show-invites"] });
  // Same reasoning for the off/travel days: they are their own key, and the Booking timeline interleaves
  // them with the shows, so a teammate adding a travel day has to repaint this client's list too.
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "tour-days"] });
  // A seatless PARTNER (ART-239) hears this on the SHOW's group (`id` is the showId there): their view and their Day of
  // Show are person-scoped keys, so they refresh whatever the id names. Absent on every band seat's cache.
  void qc.invalidateQueries({ queryKey: ["me", "partner"] });
};

/**
 * `opportunity-changed` → invalidate the band's opportunities + the person's invites.
 *
 * The signal rides BOTH `ac.ui.band.{bandId}` and `ac.ui.user.{userId}`, so `signal.id` is the
 * bandId on the band scope and the personId on the user scope. We invalidate coarsely on both: the band list
 * `['band', bandId, 'opportunities']` AND the person-scoped `['me', 'opportunities']` (id-agnostic — the
 * person keys never carry the id, like notifications). REST is the source of truth; a redundant refetch is fine.
 */
const invalidateOpportunities: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "opportunities"] });
  void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
};

/**
 * `settlement-updated` → invalidate the show's settlement + my-pay. Like the guest list, the
 * signal rides `ac.ui.show.{showId}` (money-GATED delivery — only money-capable connections in the show group
 * ever receive it), so `signal.id` is the showId, never a bandId. We invalidate the money sub-resource
 * keys by predicate — `['band', *, 'show', showId, 'settlement' | 'my-pay']` — for the same reason as guests:
 * a show belongs to exactly one band and switchBand scorches the cache, so at most one band's key is present.
 * REST refetches under the money gate (the signal carries no money value).
 */
const invalidateSettlement: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey;
      // …and the band's money LIST (`['band', *, 'shows', 'money', filters]`): its per-show `ticketsSold` and
      // money figures are composed from this same settlement (ART-252), so the home Ticket sales card and the
      // Booking money row must move when a count is typed on the worksheet.
      if (k[0] === "band" && k[2] === "shows" && k[3] === "money") return true;
      return (
        k.length === 5 && k[0] === "band" && k[2] === "show" && k[3] === signal.id &&
        (k[4] === "settlement" || k[4] === "my-pay")
      );
    },
  });
};

/**
 * `roster-changed` → invalidate the org's team roster. The signal rides `ac.ui.org.{orgId}`
 * (published by `assign_artist` / `unassign_artist` / `remove_org_seat`, `OrgTeamService`), so `signal.id` is
 * the orgId. We invalidate the `['org', orgId, 'team']` prefix — both the member roster (`…'team','members'`)
 * and the pending invites (`…'team','invites'`) — coarsely; REST is the source of truth, so a redundant refetch
 * is cheap and never wrong. NOTE: the org shell does not mount RealtimeProvider until Phase 7, so this handler
 * is wired-but-dormant today (the mutation hooks already invalidate on write); it is the correct seam.
 */
const invalidateRoster: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["org", signal.id, "team"] });
};

/**
 * `roster-todos-changed` → invalidate the org's cross-roster to-dos widget. The signal
 * rides `ac.ui.org.{orgId}` (fanned by the task producer to every org that reaches the task's band), so
 * `signal.id` is the orgId. We invalidate the `['org', orgId, 'roster', 'todos']` prefix — a partial match that
 * covers EVERY filter variant (per-artist / assigned-to-me / Archive) at once. Coarse by design: the REST
 * endpoint re-enforces per-viewer reach on refetch, so an unassigned staffer who receives it refetches to zero
 * rows (correct). REST is the source of truth; a redundant refetch is cheap and never wrong.
 */
const invalidateRosterTodos: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["org", signal.id, "roster", "todos"] });
};

/**
 * `task-changed` → invalidate the band's tasks + the caller's cross-band assignments.
 *
 * The signal rides BOTH `ac.ui.band.{bandId}` and `ac.ui.user.{userId}` — task edits fan to the band
 * (the widget) AND to the assignee's personal digest — so `signal.id` is the bandId on the band scope and the
 * personId on the user scope. We invalidate coarsely on both: the band-scoped `['band', bandId, 'tasks']`
 * (partial match → every filtered widget view + the badge) AND the person-scoped `['me','tasks']` (id-agnostic,
 * like notifications). REST is the source of truth; a redundant refetch is cheap and never wrong. We do NOT
 * blanket-invalidate the open drawer's `['band', bandId, 'task', taskId]` here — the signal carries no taskId
 * on the band scope, and the drawer refetches on its own settle; the list repaint is what the badge needs.
 */
const invalidateTasks: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "tasks"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "task"] });
  void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
  // A RELEASE-scoped task edit changes the composed release's To-do counts (the Overview checklist KPI +
  // Needs-Attention + the hub spotlight %). The composed record is keyed under 'release' and task edits emit
  // `task-changed` (not `release-changed`), so refresh open command-center records here too. Coarse
  // and cheap — only mounted release queries refetch; a no-op everywhere else.
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "release"] });
};

/**
 * `release-changed` → invalidate the band's releases hub + every open command-center record (
 * ). The signal rides `ac.ui.band.{bandId}`, so `signal.id` is the bandId and there is no
 * releaseId on the wire (a band-group signal is id-only) — EVERY release mutation (root edit + any child:
 * assets/dsp-links/credits/milestones/budget/smart-link) bumps the root e_ver and re-emits it. We invalidate
 * coarsely: the hub-list `['band', bandId, 'releases']` (partial → every status-filtered view + the spotlight)
 * AND every command-center record `['band', bandId, 'release', …]` (partial → the composed doc + its shares
 * sub-key). REST is the source of truth; a redundant refetch is cheap and never wrong.
 */
const invalidateReleases: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "releases"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "release"] });
};

/**
 * The registry: frozen dash-form signal `type` → query action. owns the guest feature's two
 * entries; adds `schedule-changed`; the remaining types register in their owning features as those
 * phases land. An unmapped or unknown type is ignored (forward compatibility).
 */
const invalidateNotifications: SignalHandler = (qc, _signal) => {
  // `notification-created` on ac.ui.user.{userId}: `id` is the person id (this client already joins its own
  // user group). It also carries `unreadCount`, but we still refetch — the registry only invalidates a key,
  // so the drawer + badge repaint from REST (the carried count is a future no-round-trip optimization). The
  // keys are person-scoped under ['me','notifications'] (NOT ['band', …]) so they survive a band-switch
  // scorch; invalidating the parent covers both the list and the unread children.
  void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
  // A partner's notification is "you're on the list" (ART-239) — their own view carries that status.
  void qc.invalidateQueries({ queryKey: ["me", "partner"] });
};

/**
 * `thread-updated` → invalidate the inbox thread lists + every open thread + the inbox badge (
 * ). The signal rides `ac.ui.user.{userId}` (this client already joins its own user group), so `signal.id`
 * is the person id. We invalidate coarsely on the PERSON-scoped keys (id-agnostic, like notifications/tasks —
 * they never carry the id, so they survive a band switch): the list parent `['me','threads']` (partial match →
 * every tab: Messages/Friends/Shows), every open thread `['me','thread']`, and the single badge source
 * `['me','inbox','badge']`. REST is the source of truth; a redundant refetch is cheap and never wrong.
 */
const invalidateThreadUpdated: SignalHandler = (qc, _signal) => {
  void qc.invalidateQueries({ queryKey: ["me", "threads"] });
  void qc.invalidateQueries({ queryKey: ["me", "thread"] });
  void qc.invalidateQueries({ queryKey: ["me", "inbox", "badge"] });
};

/**
 * `badge` → refresh the ONE inbox unread-badge source. The signal rides `ac.ui.user.{userId}`;
 * we invalidate `['me','inbox','badge']` so the topbar/tab-bar/app-icon badge (all reading `useInboxBadge`)
 * repaint from REST — one source, so they never disagree.
 */
const invalidateBadge: SignalHandler = (qc, _signal) => {
  void qc.invalidateQueries({ queryKey: ["me", "inbox", "badge"] });
};

/**
 * `connection-changed` → invalidate the band's connection cards (row 9). Rides
 * `ac.ui.band.{bandId}`, so `signal.id` is the bandId; the connections query is keyed
 * `['band', bandId, 'connections']` (band-scoped so a band switch scorches it). Closes the OAuth loop live (a
 * connect/disconnect flips the card without popup poll). UNCONDITIONAL — the card is a small band-scoped list, so
 * a per-signal v-drop would risk dropping a legitimate second event; REST is the source of truth, so a redundant
 * refetch is cheap.
 */
const invalidateConnections: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "connections"] });
  // The link-in-bio editor's AUTO rows are DERIVED from healthy connections, so a connect/disconnect must
  // repaint them live. Safe against the draft-clobber: LinksEditor is NOT remounted by a dataUpdatedAt
  // key — its manual-row draft is local state, so this refetch refreshes the read-only auto rows without wiping edits.
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "link-in-bio"] });
};

/**
 * `broadcast-changed` → invalidate the band's broadcast history + any open per-broadcast stats (row,
 * ). Rides `ac.ui.band.{bandId}`, so `signal.id` is the bandId; the history is keyed
 * `['band', bandId, 'broadcasts']` and a per-broadcast stats read `['band', bandId, 'broadcast', id]`. A logged
 * broadcast repaints the history live (roster-side too, Phase 7). UNCONDITIONAL — a small band-scoped list; REST
 * is the source of truth, so a redundant refetch is cheap. (Fans have no server UI-signal — fan mutations
 * invalidate locally via the mutation hooks' onSuccess.)
 */
const invalidateBroadcasts: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "broadcasts"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "broadcast"] });
};

/**
 * `sync-completed` → CO-OWNED by connections AND the analytics stats reads. All ride
 * `ac.ui.band.{bandId}`. A background sync landing re-stamps the connection card's "last synced" AND repaints the
 * dashboard analytics widgets AND the Music tab AND the Content tab AND the Marketing overview with the fresh
 * `as_of`, so the handler invalidates every key — the connections list (`['band', bandId, 'connections']`), the
 * composed dashboard read (`['band', bandId, 'dashboard-stats']`), the composed Music read (`['band', bandId,
 * 'music-stats']`), the composed Content read (`['band', bandId, 'content-stats']`), and the composed Marketing
 * overview (`['band', bandId, 'marketing-overview']`). MUST invalidate all, never just one (the /
 * merges — do not narrow it back).
 */
const invalidateSyncCompleted: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "connections"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "dashboard-stats"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "music-stats"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "content-stats"] });
  // the Marketing overview hero/releases numbers also come from the sync-landed projections — repaint them
  // on a background sync too (do NOT narrow the connections/dashboard/music/content invalidations above).
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "marketing-overview"] });
};

/**
 * `road-cases-changed` → invalidate the band's road cases. Rides `ac.ui.band.{bandId}`,
 * so `signal.id` is the bandId; the list is keyed `['band', bandId, 'road-cases']` (band-scoped → a band switch
 * scorches it). A create / pair / unpair / scan-toggle (native NFC tap OR the authed web fallback) repaints the
 * mobile Case-Checklist widget AND the road-cases screen live. UNCONDITIONAL — a small band-scoped list produced
 * with `v=0`; REST is the source of truth, so a redundant refetch is cheap and never wrong.
 */
const invalidateRoadCases: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "road-cases"] });
};

/**
 * `brand-changed` → invalidate the brand itself AND everything DERIVED from it.
 *
 * The derived half is the whole reason this signal exists. The shell's band chip renders the main photo from
 * `avatarMediaId` on `/me`, and the Link-in-Bio tour row renders the live photo from `iconMediaId` on its own
 * query — both hold a media id minted by a DIFFERENT query, so saving a new photo left them pointing at the
 * previous one until something unrelated refetched them. Every seat with the band open repaints, not just the
 * tab that saved.
 *
 * The image BYTES never need busting: an upload mints a new media id, so a replaced photo is a different URL —
 * which is exactly why the variant responses can be cached `immutable`. Only the reference goes stale.
 *
 * UNCONDITIONAL — "the brand" is five photo rows plus a branding row, so there is no single monotonic version to
 * compare (the `guests-changed` reasoning); the producer sends `v=0` and REST is the source of truth.
 */
const invalidateBrand: SignalHandler = (qc, signal) => {
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "brand-photos"] });
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "brand-branding"] });
  // Derived consumers — the point of the signal.
  void qc.invalidateQueries({ queryKey: ["me"] });                                  // seats → the shell's band chip
  void qc.invalidateQueries({ queryKey: ["band", signal.id, "link-in-bio"] });      // tour row's live photo
};

const SIGNAL_HANDLERS: Partial<Record<UiSignalType, SignalHandler>> = {
  "guests-changed": invalidateGuestList,
  "guest-scanned": invalidateGuestList,
  "schedule-changed": invalidateSchedule,
  "opportunity-changed": invalidateOpportunities,
  "notification-created": invalidateNotifications,
  "settlement-updated": invalidateSettlement,
  "roster-changed": invalidateRoster,
  "roster-todos-changed": invalidateRosterTodos,
  "task-changed": invalidateTasks,
  "release-changed": invalidateReleases,
  "thread-updated": invalidateThreadUpdated,
  badge: invalidateBadge,
  "connection-changed": invalidateConnections,
  "broadcast-changed": invalidateBroadcasts,
  "sync-completed": invalidateSyncCompleted,
  "road-cases-changed": invalidateRoadCases,
  "brand-changed": invalidateBrand,
};

/** Apply one plane-B signal via the registry. Unknown/unmapped types are ignored. */
export function applySignal(qc: QueryClient, signal: UiSignal): void {
  SIGNAL_HANDLERS[signal.type]?.(qc, signal);
}

/**
 * The reconnect catch-up (GATE-4): every (re)connect emits a synthetic `connected`, and
 * the app refetches EVERY subscribed scope UNCONDITIONALLY — no version check (the hello frame's `versions`
 * map is reserved-empty at go-live; version-filtered refetch is post-launch). Invalidating all active queries
 * is the MVP's proven "the same initial page load runs anyway" contract and is a strict superset of "every
 * subscribed scope". REST is the source of truth, so a redundant refetch is cheap and never wrong.
 */
export function refetchAllScopes(qc: QueryClient): void {
  void qc.invalidateQueries();
}
